import { chatApiBase } from './chatFetch';

// Push-to-talk dictation against POST /chat/transcribe on the same chat-api
// the rest of the composer uses (localhost proxy included).

export const TRANSCRIBE_URL = `${chatApiBase}/transcribe`;

// ~3MB of audio. Matches MAX_AUDIO_BASE64_CHARS (4_000_000) on the route:
// base64 expands bytes by 4/3.
export const MAX_AUDIO_BYTES = 3_000_000;

export const MAX_RECORDING_MS = 120_000;

export type TranscribeModelOption = { id: string; name: string };

// Used when GET /chat/transcribe/models fails. First entry is the default.
export const FALLBACK_TRANSCRIBE_MODELS: TranscribeModelOption[] = [
  { id: 'spacexai/grok-stt', name: 'Grok STT' },
  { id: 'openai/gpt-4o-transcribe', name: 'GPT-4o Transcribe' },
  { id: 'openai/gpt-4o-mini-transcribe', name: 'GPT-4o mini Transcribe' },
  { id: 'google/gemini-3.5-transcribe', name: 'Gemini 3.5 Transcribe' },
  { id: 'openai/whisper-1', name: 'Whisper' },
];

const TRANSCRIBE_MODEL_STORAGE_KEY = 'crm.chat.transcribeModel';

export const transcribeModelsUrl = (transcribeUrl: string): string => {
  // chatApiBase is a path on localhost (`/local-chat-api`), an absolute URL elsewhere.
  const url = new URL(transcribeUrl, 'https://transcribe.invalid');
  const path = url.pathname.replace(/\/$/, '');
  url.pathname = `${path}/models`;
  if (url.origin === 'https://transcribe.invalid') {
    return `${url.pathname}${url.search}`;
  }
  return url.toString();
};

export const parseTranscribeModelsBody = (
  body: unknown,
): {
  models: TranscribeModelOption[];
  defaultModelId: string | null;
  error: string | null;
} => {
  if (typeof body !== 'object' || body == null) {
    return { models: [], defaultModelId: null, error: null };
  }
  const record = body as Record<string, unknown>;
  const models = Array.isArray(record.models)
    ? record.models.flatMap(entry => {
        if (typeof entry !== 'object' || entry == null) {
          return [];
        }
        const model = entry as Record<string, unknown>;
        if (typeof model.id !== 'string' || model.id.length === 0) {
          return [];
        }
        const name =
          typeof model.name === 'string' && model.name.trim().length > 0
            ? model.name.trim()
            : model.id;
        return [{ id: model.id, name }];
      })
    : [];
  const defaultModelId =
    typeof record.defaultModelId === 'string' &&
    models.some(model => model.id === record.defaultModelId)
      ? record.defaultModelId
      : models[0]?.id ?? null;
  const error =
    typeof record.error === 'string' && record.error.length > 0
      ? record.error
      : null;
  return { models, defaultModelId, error };
};

export const resolveTranscribeModelId = (
  stored: string | null,
  models: readonly TranscribeModelOption[] = FALLBACK_TRANSCRIBE_MODELS,
): string | null => {
  if (stored == null) {
    return null;
  }
  return models.some(model => model.id === stored) ? stored : null;
};

export const readStoredTranscribeModelId = (): string | null => {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const stored = localStorage.getItem(TRANSCRIBE_MODEL_STORAGE_KEY);
    return stored != null && stored.length > 0 ? stored : null;
  } catch {
    return null;
  }
};

export const writeStoredTranscribeModelId = (id: string): void => {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(TRANSCRIBE_MODEL_STORAGE_KEY, id);
  } catch {
    // Storage unavailable; the choice just won't persist.
  }
};

export const clearStoredTranscribeModelId = (): void => {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(TRANSCRIBE_MODEL_STORAGE_KEY);
  } catch {
    // Storage unavailable; the rejected id may be sent again next load.
  }
};

export const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
] as const;

export const mediaTypeEssence = (mimeType: string): string =>
  (mimeType.split(';')[0] ?? '').trim().toLowerCase();

export const pickRecorderMimeType = (
  isTypeSupported: (mimeType: string) => boolean,
): string | null =>
  RECORDER_MIME_CANDIDATES.find(mimeType => isTypeSupported(mimeType)) ?? null;

export const isRetryableTranscribeStatus = (status: number): boolean =>
  status === 502 || status === 504;

// Only this 400 means "the model id was rejected"; other 400s (bad audio,
// too large) would fail the same way on a retry.
export const isModelRejectedBody = (body: unknown): boolean =>
  typeof body === 'object' &&
  body != null &&
  (body as Record<string, unknown>).code === 'model_not_allowed';

// The route forwards the raw upstream body as `gatewayBody`; the actionable
// reason ("audio could not be decoded", "only supports streaming") is in its
// error.message, not in the route's generic `error`.
const gatewayErrorDetail = (gatewayBody: unknown): string | null => {
  if (typeof gatewayBody !== 'string' || gatewayBody.length === 0) {
    return null;
  }
  try {
    const parsed = JSON.parse(gatewayBody) as {
      error?: { message?: unknown } | string;
    };
    const message =
      typeof parsed.error === 'string' ? parsed.error : parsed.error?.message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
  } catch {
    // Not JSON (e.g. an infrastructure error page): show it raw.
  }
  return gatewayBody.slice(0, 500);
};

export const transcriptFromBody = (
  body: unknown,
): { text: string } | { error: string } | { code: 'not-json' | 'no-text' } => {
  if (typeof body !== 'object' || body == null) {
    return { code: 'not-json' };
  }
  const record = body as Record<string, unknown>;
  if (typeof record.error === 'string' && record.error.length > 0) {
    const detail = gatewayErrorDetail(record.gatewayBody);
    return { error: detail ? `${record.error} ${detail}` : record.error };
  }
  if (typeof record.text !== 'string') {
    return { code: 'no-text' };
  }
  return { text: record.text };
};

export const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read the recording.'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error('Could not read the recording.'));
    reader.readAsDataURL(blob);
  });
